
import { useState } from 'react';
import { alertHistory, alertTrends } from '../../mocks/alertHistoryData';

export default function HistorialAlertas() {
  const [filter, setFilter] = useState<'all' | 'critical' | 'warning' | 'resolved'>('all');
  const [dateRange, setDateRange] = useState('30dias');
  const [showTrends, setShowTrends] = useState(true);

  const filteredAlerts = alertHistory.filter(alert => {
    if (filter === 'all') return true;
    if (filter === 'resolved') return alert.resolved;
    return alert.type === filter;
  });

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'critical': return 'ri-alarm-warning-fill';
      case 'warning': return 'ri-alert-fill';
      default: return 'ri-information-fill';
    }
  };

  const getAlertColor = (type: string, resolved: boolean) => {
    if (resolved) return 'bg-gray-100 border-gray-200 text-gray-600';
    switch (type) {
      case 'critical': return 'bg-red-50 border-red-200 text-red-800';
      case 'warning': return 'bg-amber-50 border-amber-200 text-amber-800';
      default: return 'bg-teal-50 border-teal-200 text-teal-800';
    }
  };

  const stats = {
    total: alertHistory.length,
    critical: alertHistory.filter(a => a.type === 'critical').length,
    warning: alertHistory.filter(a => a.type === 'warning').length,
    resolved: alertHistory.filter(a => a.resolved).length,
    avgResolutionTime: '2.5h'
  };

  return (
    <div className="space-y-6">
      {/* Estadísticas Generales */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-xl shadow-lg p-4 text-center">
          <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-2">
            <i className="ri-notification-3-line text-slate-600 text-xl"></i>
          </div>
          <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
          <p className="text-xs text-slate-500">Total Alertas</p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-4 text-center">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-2">
            <i className="ri-alarm-warning-fill text-red-500 text-xl"></i>
          </div>
          <p className="text-2xl font-bold text-red-600">{stats.critical}</p>
          <p className="text-xs text-slate-500">Críticas</p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-4 text-center">
          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-2">
            <i className="ri-alert-fill text-amber-500 text-xl"></i>
          </div>
          <p className="text-2xl font-bold text-amber-600">{stats.warning}</p>
          <p className="text-xs text-slate-500">Advertencias</p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-4 text-center">
          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
            <i className="ri-checkbox-circle-fill text-green-500 text-xl"></i>
          </div>
          <p className="text-2xl font-bold text-green-600">{stats.resolved}</p>
          <p className="text-xs text-slate-500">Resueltas</p>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-4 text-center">
          <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-2">
            <i className="ri-time-line text-cyan-500 text-xl"></i>
          </div>
          <p className="text-2xl font-bold text-cyan-600">{stats.avgResolutionTime}</p>
          <p className="text-xs text-slate-500">Tiempo Promedio</p>
        </div>
      </div>

      {/* Gráfico de Tendencias */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-xl flex items-center justify-center">
              <i className="ri-line-chart-line text-white text-xl"></i>
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-800">Tendencias de Alertas</h3>
              <p className="text-sm text-slate-500">Evolución en el tiempo</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 cursor-pointer"
            >
              <option value="7dias">7 días</option>
              <option value="30dias">30 días</option>
              <option value="90dias">90 días</option>
            </select>
            <button
              onClick={() => setShowTrends(!showTrends)}
              className={`px-3 py-2 rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap ${
                showTrends ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-600'
              }`}
            >
              <i className="ri-eye-line mr-1"></i>
              {showTrends ? 'Ocultar' : 'Mostrar'}
            </button>
          </div>
        </div>

        {showTrends && (
          <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-xl p-4">
            <svg viewBox="0 0 700 200" className="w-full h-48">
              {/* Grid */}
              <defs>
                <linearGradient id="criticalGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#ef4444" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#ef4444" stopOpacity="0" />
                </linearGradient>
                <linearGradient id="warningGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#f59e0b" stopOpacity="0" />
                </linearGradient>
              </defs>

              {/* Líneas horizontales */}
              {[0, 1, 2, 3, 4].map(i => (
                <line key={i} x1="50" y1={30 + i * 35} x2="650" y2={30 + i * 35} stroke="#e5e7eb" strokeWidth="1" />
              ))}

              {/* Área críticas */}
              <polygon
                points={`50,170 ${alertTrends.map((d, i) => `${50 + i * 60},${170 - d.critical * 15}`).join(' ')} 650,170`}
                fill="url(#criticalGradient)"
              />
              
              {/* Área advertencias */}
              <polygon
                points={`50,170 ${alertTrends.map((d, i) => `${50 + i * 60},${170 - d.warning * 10}`).join(' ')} 650,170`}
                fill="url(#warningGradient)"
              />

              {/* Línea críticas */}
              <polyline
                points={alertTrends.map((d, i) => `${50 + i * 60},${170 - d.critical * 15}`).join(' ')}
                fill="none"
                stroke="#ef4444"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />

              {/* Línea advertencias */}
              <polyline
                points={alertTrends.map((d, i) => `${50 + i * 60},${170 - d.warning * 10}`).join(' ')}
                fill="none"
                stroke="#f59e0b"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />

              {/* Línea resueltas */}
              <polyline
                points={alertTrends.map((d, i) => `${50 + i * 60},${170 - d.resolved * 8}`).join(' ')}
                fill="none"
                stroke="#10b981"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray="5,5"
              />

              {/* Puntos críticas */}
              {alertTrends.map((d, i) => (
                <circle key={`c-${i}`} cx={50 + i * 60} cy={170 - d.critical * 15} r="5" fill="#ef4444" />
              ))}

              {/* Puntos advertencias */}
              {alertTrends.map((d, i) => (
                <circle key={`w-${i}`} cx={50 + i * 60} cy={170 - d.warning * 10} r="5" fill="#f59e0b" />
              ))}

              {/* Etiquetas eje X */}
              {alertTrends.map((d, i) => (
                <text key={i} x={50 + i * 60} y="190" textAnchor="middle" fontSize="10" fill="#6b7280">
                  {d.date}
                </text>
              ))}

              {/* Etiquetas eje Y */}
              {[0, 2, 4, 6, 8].map((val, i) => (
                <text key={i} x="40" y={170 - val * 15} textAnchor="end" fontSize="10" fill="#6b7280" dominantBaseline="middle">
                  {val}
                </text>
              ))}
            </svg>

            {/* Leyenda */}
            <div className="flex justify-center space-x-6 mt-4">
              <div className="flex items-center space-x-2">
                <span className="w-4 h-1 bg-red-500 rounded"></span>
                <span className="text-xs text-gray-600">Críticas</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="w-4 h-1 bg-amber-500 rounded"></span>
                <span className="text-xs text-gray-600">Advertencias</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="w-4 h-0.5 bg-green-500 rounded" style={{ borderStyle: 'dashed' }}></span>
                <span className="text-xs text-gray-600">Resueltas</span>
              </div>
            </div>
          </div>
        )}

        {/* Insights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <div className="p-4 bg-green-50 rounded-xl border border-green-200">
            <div className="flex items-center space-x-2 mb-2">
              <i className="ri-arrow-down-line text-green-600"></i>
              <span className="text-sm font-semibold text-green-700">Tendencia Positiva</span>
            </div>
            <p className="text-xs text-green-600">Las alertas críticas han disminuido un 25% respecto al mes anterior</p>
          </div>
          <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
            <div className="flex items-center space-x-2 mb-2">
              <i className="ri-focus-3-line text-amber-600"></i>
              <span className="text-sm font-semibold text-amber-700">Área de Atención</span>
            </div>
            <p className="text-xs text-amber-600">El pH presenta mayor frecuencia de alertas los fines de semana</p>
          </div>
          <div className="p-4 bg-cyan-50 rounded-xl border border-cyan-200">
            <div className="flex items-center space-x-2 mb-2">
              <i className="ri-timer-line text-cyan-600"></i>
              <span className="text-sm font-semibold text-cyan-700">Tiempo de Respuesta</span>
            </div>
            <p className="text-xs text-cyan-600">El tiempo promedio de resolución mejoró en 30 minutos</p>
          </div>
        </div>
      </div>

      {/* Historial de Alertas */}
      <div className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-slate-400 to-slate-600 rounded-xl flex items-center justify-center">
              <i className="ri-history-line text-white text-xl"></i>
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-800">Historial de Alertas</h3>
              <p className="text-sm text-slate-500">Registro completo de eventos</p>
            </div>
          </div>
          <div className="flex space-x-2">
            {[
              { key: 'all', label: 'Todas', count: stats.total },
              { key: 'critical', label: 'Críticas', count: stats.critical },
              { key: 'warning', label: 'Advertencias', count: stats.warning },
              { key: 'resolved', label: 'Resueltas', count: stats.resolved }
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key as typeof filter)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  filter === f.key
                    ? 'bg-teal-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {f.label} ({f.count})
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-3 max-h-96 overflow-y-auto">
          {filteredAlerts.map((alert) => (
            <div
              key={alert.id}
              className={`p-4 rounded-xl border-2 transition-all ${getAlertColor(alert.type, alert.resolved)}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    alert.resolved ? 'bg-gray-200' :
                    alert.type === 'critical' ? 'bg-red-200' : 'bg-amber-200'
                  }`}>
                    <i className={`${alert.resolved ? 'ri-checkbox-circle-fill text-gray-500' : getAlertIcon(alert.type)} ${
                      alert.resolved ? '' :
                      alert.type === 'critical' ? 'text-red-600' : 'text-amber-600'
                    } text-xl`}></i>
                  </div>
                  <div>
                    <div className="flex items-center space-x-2 mb-1">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        alert.resolved ? 'bg-gray-200 text-gray-600' :
                        alert.type === 'critical' ? 'bg-red-200 text-red-700' : 'bg-amber-200 text-amber-700'
                      }`}>
                        {alert.resolved ? 'Resuelta' : alert.type === 'critical' ? 'Crítica' : 'Advertencia'}
                      </span>
                      <span className="text-xs text-gray-500">{alert.parameter}</span>
                    </div>
                    <p className="text-sm font-medium mb-1">{alert.message}</p>
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span><i className="ri-calendar-line mr-1"></i>{alert.date}</span>
                      <span><i className="ri-time-line mr-1"></i>{alert.time}</span>
                      <span><i className="ri-user-line mr-1"></i>{alert.technician}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold">{alert.value} <span className="text-xs font-normal">{alert.unit}</span></p>
                  <p className="text-xs text-gray-500">Rango: {alert.optimalRange}</p>
                </div>
              </div>
              {alert.resolved && alert.resolution && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-600">
                    <i className="ri-check-double-line mr-1 text-green-500"></i>
                    <strong>Resolución:</strong> {alert.resolution}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    <i className="ri-time-line mr-1"></i>
                    Resuelto en: {alert.resolutionTime}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {filteredAlerts.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-checkbox-circle-fill text-5xl text-green-400 mb-4"></i>
            <p className="text-gray-600 font-medium">No hay alertas en esta categoría</p>
            <p className="text-sm text-gray-400">Todos los parámetros están funcionando correctamente</p>
          </div>
        )}
      </div>
    </div>
  );
}
